import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { formatDateTime } from '../../../shared/utils/formatDate';
import SearchField from '../../../shared/components/SearchField';
import StatusPill from '../../../shared/components/pill/StatusPill';
import {
  formatTaskPeopleLine,
  formatTaskTitleLine,
  taskTicketFilterValue,
} from '../../utils/taskTicketDisplay';

export default function TaskTicketList({
  items,
  onItemClick,
  loading = false,
  error = null,
  emptyMessage = 'No tasks found.',
  filterPlaceholder = 'Search tasks…',
  filterExtras = null,
  hideFilter = false,
  hideAssetName = false,
  onAssign = null,
  className = '',
}) {
  const [query, setQuery] = useState('');

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((task) => taskTicketFilterValue(task).includes(q));
  }, [items, query]);

  return (
    <div className={className}>
      {!hideFilter && (
        <div className="mb-3">
          <SearchField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={filterPlaceholder}
          />
          {filterExtras && <div className="mt-2 d-flex flex-wrap gap-3">{filterExtras}</div>}
        </div>
      )}

      {loading && (
        <div className="text-muted small py-3 d-flex align-items-center gap-2">
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          Loading…
        </div>
      )}

      {!loading && error && <div className="alert alert-danger py-2 small mb-0">{error}</div>}

      {!loading && !error && filteredItems.length === 0 && (
        <div className="text-muted small py-2">{emptyMessage}</div>
      )}

      {!loading && !error && filteredItems.length > 0 && (
        <ul className="list-group list-group-flush">
          {filteredItems.map((task) => {
            const peopleLine = formatTaskPeopleLine(task);
            const isOpen = !task.completed_at;
            const showAssign = isOpen && !task.assigned_to_email && onAssign;

            return (
              <li key={task.id} className="list-group-item px-0">
                <div className="d-flex justify-content-between gap-3 align-items-start">
                  <div className="min-w-0 flex-grow-1">
                    {/* The asset sits above the link rather than inside it. "Asset: Workflow"
                        was one truncating line, so on any narrow card the workflow name — the
                        thing that says what the task is — was the half that got cut. It is also
                        not what the link goes to: the link opens the task. */}
                    {!hideAssetName && task.asset?.name && (
                      <div className="text-muted text-uppercase text-truncate" style={{ fontSize: '0.7rem', letterSpacing: '.04em' }}>
                        {task.asset.name}
                      </div>
                    )}
                    <button
                      type="button"
                      className="btn btn-link p-0 text-start text-decoration-none w-100"
                      onClick={() => onItemClick?.(task)}
                    >
                      <div className="fw-medium text-truncate">
                        {formatTaskTitleLine(task, { hideAssetName: true })}
                      </div>
                      {peopleLine && (
                        <div className="text-muted small text-truncate">{peopleLine}</div>
                      )}
                    </button>
                    {showAssign && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-primary"
                        onClick={() => onAssign(task)}
                      >
                        Assign
                      </button>
                    )}
                  </div>
                  <div className="text-end flex-shrink-0 small">
                    <StatusPill tone={task.completed_at ? 'neutral' : 'success'}>
                      {task.completed_at ? 'Closed' : 'Open'}
                    </StatusPill>
                    <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                      {formatDateTime(task.updated_at)}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

TaskTicketList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  onItemClick: PropTypes.func,
  loading: PropTypes.bool,
  error: PropTypes.string,
  emptyMessage: PropTypes.string,
  filterPlaceholder: PropTypes.string,
  filterExtras: PropTypes.node,
  hideFilter: PropTypes.bool,
  hideAssetName: PropTypes.bool,
  onAssign: PropTypes.func,
  className: PropTypes.string,
};

TaskTicketList.defaultProps = {
  onItemClick: null,
  loading: false,
  error: null,
  emptyMessage: 'No tasks found.',
  filterPlaceholder: 'Search tasks…',
  filterExtras: null,
  hideFilter: false,
  hideAssetName: false,
  onAssign: null,
  className: '',
};
